import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import * as Dialog from '@radix-ui/react-dialog';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import Button from './Button.tsx';
import { withIcon } from './Icon.tsx';
import classnames from '../utils/classnames.tsx';

const CloseIcon = withIcon(faXmark);

interface DrawerProps {
    title: React.ReactNode;
    visible: boolean;
    onClose: () => void;
    children: React.ReactNode;
    className?: string;
}

/** A panel that slides in from the right over the page, for a task next to the content (e.g. testing an assistant).
 *  Radix Dialog: overlay, Esc, focus trap and close come from it. */
const Drawer = ({ title, visible, onClose, children, className }: DrawerProps) => {
    const { t } = useTranslation('core');
    const container = document.getElementById('modal-root');
    if (!container) return null;

    return (
        <Dialog.Root open={visible} onOpenChange={(open) => !open && onClose()}>
            <Dialog.Portal container={container}>
                {/* same scrim as Modal until the surface-overlay token lands (DESIGN.md Decided 2026-09-04) */}
                <Dialog.Overlay className="fixed inset-0 z-[21000] bg-[rgba(0,0,0,0.75)]" />
                <Dialog.Content
                    aria-describedby={undefined}
                    onOpenAutoFocus={(event) => event.preventDefault()}
                    className={classnames(
                        'fixed right-0 top-0 z-[21000] flex h-full w-[40rem] max-w-full flex-col bg-surface-weak shadow-lg',
                        'data-[state=open]:animate-slide-in-from-right data-[state=closed]:animate-slide-out-to-right',
                        'js-modal', // lifts popovers (date picker, selects) above the drawer, as in Modal
                        className
                    )}
                >
                    <div className="flex h-14 flex-none items-center justify-between gap-4 border-b border-solid border-line-weak px-6">
                        <Dialog.Title className="m-0 text-base font-semibold text-content-strong">{title}</Dialog.Title>
                        <Dialog.Close asChild>
                            <Button
                                variant="ghost"
                                aria-label={t('components.confirmModal.close')}
                                className="-mr-2 flex h-8 w-8 items-center justify-center p-0 text-content-strong"
                            >
                                <CloseIcon />
                            </Button>
                        </Dialog.Close>
                    </div>
                    <div className="flex flex-1 flex-col gap-6 overflow-auto p-6">{children}</div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

export default Drawer;
